import { Suspense, lazy, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/types";
import { formatMoney, formatPercent, moneySign } from "@/shared/lib";
import { tactile } from "@/shared/telegram";
import { Panel, Segmented, type SegmentedOption } from "@/shared/ui";

type ChartPoint = components["schemas"]["ChartPoint"];
type ChartPeriod = components["schemas"]["ChartPeriod"];

/**
 * Тик по спаду `busy`: ряд догрузился и уже нарисован.
 *
 * Первый рендер молчит — экран открылся с данными, а не «дозагрузил» их, и
 * тик там был бы отзывом на действие, которого не было.
 */
function useLoadedTick(busy: boolean): void {
  const wasBusy = useRef(false);

  useEffect(() => {
    if (wasBusy.current && !busy) tactile("select");
    wasBusy.current = busy;
  }, [busy]);
}

/** Порядок чипов — от короткого к длинному, как в макете. */
const PERIODS: readonly ChartPeriod[] = ["24h", "7d", "30d", "90d"] as const;

/** Периоды с почасовым шагом: на них в подписи тултипа есть смысл в часе. */
const HOURLY: ReadonlySet<ChartPeriod> = new Set<ChartPeriod>(["24h", "7d"]);

const EquityPlot = lazy(() =>
  import("./EquityPlot").then((module) => ({ default: module.EquityPlot })),
);

/**
 * Figma: компонент `EquityChart` (14:85), вариант `Range=30D` (14:31).
 * Документация компонента: «Net P&L after fees. Benchmark and AI decision
 * markers remain visible.»
 *
 * Легенда в макете объявляет три ряда — Net, BTC и AI event. Бэкенд отдаёт
 * только Net (`ChartPoint`), рядов сравнения с BTC и отметок решений AI в
 * контракте нет. Рисовать пустые легенды нельзя: они обещают данные, которых
 * не будет. Поэтому в легенде остаётся один ряд (DESIGN.md §6).
 *
 * Чип периода из макета — теперь переключатель, а не подпись (FE-05). Пока
 * начисление было суточным, переключать было нечего: длина ряда менялась, а
 * форма линии — нет. С почасовым начислением период задаёт ещё и шаг точки, и
 * `24Ч` показывает то, ради чего всё затевалось, — как робот торговал внутри
 * суток.
 *
 * Сам ряд и его шаг считает бэкенд (правило ТЗ №5): сюда приходит готовое, а
 * период уезжает в запрос.
 *
 * Отклик (FE-14): нажатие на чип отзывается делегатом, а приход нового ряда —
 * ещё одним тихим тиком. Иначе переключение периода выглядит как «нажал и
 * жди»: чипы заблокированы, график тот же, и непонятно, случилось ли что-то.
 */
interface EquityChartProps {
  points: ChartPoint[];
  /** Итог за период — приходит с бэкенда готовым, клиент его не считает. */
  total: string;
  totalPct: string;
  period: ChartPeriod;
  onPeriodChange: (period: ChartPeriod) => void;
  /** Идёт ли запрос нового периода — чипы на это время блокируются. */
  busy?: boolean;
}

export function EquityChart({
  points,
  total,
  totalPct,
  period,
  onPeriodChange,
  busy = false,
}: EquityChartProps) {
  const { t } = useTranslation();
  const sign = moneySign(total);
  useLoadedTick(busy);

  const periods = useMemo<readonly SegmentedOption<ChartPeriod>[]>(
    () => PERIODS.map((item) => ({ value: item, label: t(`home.periods.${item}`) })),
    [t],
  );

  return (
    <Panel as="section" tone="green" radius="lg" className="flex w-full flex-col gap-[14px] p-4">
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-[3px]">
          <span className="text-caption font-medium text-text-secondary">{t("home.chartTitle")}</span>
          <div className="flex items-start gap-2">
            <span className="text-h3 font-semibold text-text-primary">
              {formatMoney(total, { sign: "always", suffix: "USDT" })}
            </span>
            <span
              className={[
                "text-label font-semibold",
                sign < 0 ? "text-status-loss" : "text-status-profit",
              ].join(" ")}
            >
              {formatPercent(totalPct, { sign: "always" })}
            </span>
          </div>
        </div>

        <Segmented
          options={periods}
          value={period}
          onChange={onPeriodChange}
          ariaLabel={t("home.periodLabel")}
          variant="solid"
          className={busy ? "w-fit shrink-0 opacity-60" : "w-fit shrink-0"}
        />
      </header>

      <div className="h-[190px] w-full">
        <Suspense fallback={<div className="shimmer size-full rounded-xl bg-background-elevated" />}>
          <EquityPlot points={points} positive={sign >= 0} hourly={HOURLY.has(period)} />
        </Suspense>
      </div>

      <footer className="flex items-start gap-4">
        <span className="flex items-center gap-1.5">
          <span className="size-[7px] rounded-full bg-chart-equity" aria-hidden="true" />
          <span className="text-micro font-medium text-text-secondary">{t("home.chartNet")}</span>
        </span>
      </footer>
    </Panel>
  );
}
